import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMsg } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * AuthCallback — one-shot Google session_id exchange.
 *
 * Mounted synchronously by App.js when `window.location.hash` contains
 * `session_id=...`. This bypasses the normal route render so no other page
 * mounts (and no premature /auth/me call) before the session cookie exists.
 *
 * Reference: /app/auth_testing.md
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const processed = useRef(false);  // guard against React StrictMode double-invoke
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setError("Missing Google session token.");
      return;
    }

    (async () => {
      try {
        const r = await api.post("/auth/google/session", { session_id: sessionId });
        const { user, needs_profile_completion } = r.data;

        // Clean the fragment so a refresh doesn't retry the exchange.
        try {
          const url = window.location.pathname + window.location.search;
          window.history.replaceState(null, "", url);
        } catch { /* noop */ }

        // Rehydrate the AuthProvider from the fresh cookie.
        await refreshUser();

        if (needs_profile_completion) {
          nav("/complete-profile", { replace: true });
          return;
        }
        const dest =
          user?.role === "admin" ? "/admin"
          : user?.role === "dealer" ? "/dealer"
          : user?.role === "builder" ? "/builder"
          : user?.role === "team" || user?.role === "team_member" ? "/team"
          : "/";
        nav(dest, { replace: true });
      } catch (err) {
        setError(apiErrorMsg(err, "Google sign-in failed"));
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps  -- one-shot exchange, nav/refreshUser stable enough

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="auth-callback-error">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl p-6 shadow-sm text-center">
          <div className="text-red-600 font-bold text-lg mb-2">Sign-in failed</div>
          <div className="text-[13px] text-zinc-600 mb-4">{error}</div>
          <button
            onClick={() => { window.location.href = "/login"; }}
            className="h-10 px-5 rounded-xl bg-[#0055FF] text-white text-[13px] font-semibold hover:bg-[#0044CC]"
            data-testid="auth-callback-retry"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50" data-testid="auth-callback-loading">
      <div className="flex flex-col items-center gap-3 text-zinc-600">
        <Loader2 className="animate-spin" size={28} />
        <div className="text-[13px] font-medium">Signing you in with Google…</div>
      </div>
    </div>
  );
}
